/**
 * RecurrenceDetailController — page /factures-recurrence-detail.html
 * (EPIC-014.4).
 *
 * Récupère le détail enrichi via `GET /recurring-invoices/{id}` :
 * toArray + `nextDates` (3 prochaines, ATOM) + `invoices` (filles) +
 * `quote` ({id, number} si quote_id non null) + `vatMismatchDetected`.
 *
 * Pilote les modales pause / reprise / archive (templates clonés au
 * premier open). Affiche conditionnellement :
 *  - bandeau D2 (mismatch TVA) si `vatMismatchDetected: true`
 *  - encart D3 « Issue du devis-cadre … » si `quote` non null
 *
 * Aucun HTML inline en JS — tout passe par cloneTemplate + fillSlots.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const STATUS_LABELS = {
  active: "active",
  paused: "en pause",
  ended: "terminée",
  archived: "archivée",
};

const STATUS_PILL_COLORS = {
  active: "olive",
  paused: "ochre",
  ended: "blue",
  archived: null,
};

const INVOICE_STATUS_LABELS = {
  draft: "brouillon",
  issued: "émise",
  sent: "envoyée",
  paid: "payée",
  overdue: "en retard",
  cancelled: "annulée",
};

export default class RecurrenceDetailController extends BaseController {
  static targets = [
    "crumbLabel",
    "title",
    "statusPill",
    "statusLabel",
    "headerDates",
    "linesRows",
    "timeline",
    "cadenceFrequency",
    "cadencePeriod",
    "cadenceMode",
    "cadencePayment",
    "cadenceReminders",
    "cadenceAttachments",
    "clientName",
    "clientLines",
    "vatMismatchBanner",
    "quoteEncart",
    "invoicesRows",
    "nextDatesList",
    "pauseBtn",
    "resumeBtn",
    "archiveModal",
    "resumeModal",
    "resumeDate",
  ];

  connect() {
    const params = new URLSearchParams(window.location.search);
    this._recurringId = params.get("id");
    if (!this._recurringId) {
      this.showError("Identifiant de récurrence manquant.");
      return;
    }
    this._load();
  }

  async _load() {
    try {
      this._detail = await api.get(`/recurring-invoices/${encodeURIComponent(this._recurringId)}`);
      this._render(this._detail);
    } catch (err) {
      const message = err instanceof APIError ? err.message : "Erreur de chargement.";
      this.showError(message);
    }
  }

  _render(detail) {
    const status = detail.status || "active";
    const statusLabel = STATUS_LABELS[status] || status;
    if (this.hasCrumbLabelTarget) {
      this.crumbLabelTarget.textContent = `Récurrence (${statusLabel})`;
    }
    this._renderHeaderState(status);
    this._renderStatusPill(status, statusLabel);
    this._renderHeaderDates(detail);
    this._renderLines(detail.lines || []);
    this._renderCadence(detail);
    this._renderTimeline(detail);
    this._renderVatMismatch(Boolean(detail.vatMismatchDetected));
    this._renderQuoteEncart(detail.quote || null);
    this._renderInvoices(detail.invoices || []);
    this._renderNextDates(detail.nextDates || []);
    this._loadClient(detail.clientId || null);
  }

  _renderStatusPill(status, statusLabel) {
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent = statusLabel;
    }
    if (this.hasStatusPillTarget) {
      const color = STATUS_PILL_COLORS[status];
      this.statusPillTarget.className = color ? `nm-pill nm-pill--${color}` : "nm-pill";
    }
  }

  _renderHeaderDates(detail) {
    if (!this.hasHeaderDatesTarget) return;
    const parts = [];
    if (detail.startDate) {
      parts.push(`Démarrée le ${this.formatDate(detail.startDate)}`);
    }
    if (detail.status === "active" && detail.nextGenerationDate) {
      parts.push(`prochaine génération le ${this.formatDate(detail.nextGenerationDate)}`);
    }
    this.headerDatesTarget.textContent = parts.join(" · ") || "—";
  }

  _renderLines(lines) {
    if (!this.hasLinesRowsTarget) return;
    this.linesRowsTarget.replaceChildren();
    if (lines.length === 0) {
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = 4;
      td.className = "muted fs-13";
      td.style.textAlign = "center";
      td.style.padding = "16px 0";
      td.textContent = "Aucune ligne définie.";
      tr.appendChild(td);
      this.linesRowsTarget.appendChild(tr);
      return;
    }
    const sorted = [...lines].sort((a, b) => (a.lineIndex ?? 0) - (b.lineIndex ?? 0));
    for (const line of sorted) {
      const fragment = this.cloneTemplate("tpl-recurrence-line-row");
      if (!fragment) continue;
      const qty = Number(line.quantity || 0);
      const unitPrice = Number(line.priceExclVat || 0);
      this.fillSlots(fragment, {
        label: line.label || "—",
        unit: line.unit ? `${this._formatQty(qty)} ${line.unit}` : "",
        quantity: this._formatQty(qty),
        unitPrice: this._formatEur(unitPrice),
        totalHt: this._formatEur(qty * unitPrice),
      });
      this.linesRowsTarget.appendChild(fragment);
    }
  }

  _renderCadence(detail) {
    const interval = Number(detail.intervalMonths || 0);
    const day = Number(detail.dayOfMonth || 0);
    if (this.hasCadenceFrequencyTarget) {
      const freq = interval === 1 ? "Tous les mois" : `Tous les ${interval} mois`;
      const dayLabel = day === 1 ? "le 1ᵉʳ" : `le ${day}`;
      this.cadenceFrequencyTarget.textContent = `${freq} · ${dayLabel}`;
    }
    if (this.hasCadencePeriodTarget) {
      const start = detail.startDate ? this.formatDate(detail.startDate) : "—";
      const end = detail.endDate ? this.formatDate(detail.endDate) : "sans échéance";
      this.cadencePeriodTarget.textContent = `Période du ${start} au ${end}`;
    }
    if (this.hasCadenceModeTarget) {
      this.cadenceModeTarget.textContent = detail.generationMode === "auto_send"
        ? "envoi automatique"
        : "brouillon à valider manuellement";
    }
    if (this.hasCadencePaymentTarget) {
      this.cadencePaymentTarget.textContent = detail.paymentProviderEnabled ? "activé" : "désactivé";
    }
    if (this.hasCadenceRemindersTarget) {
      this.cadenceRemindersTarget.textContent = detail.remindersEnabled ? "activées" : "désactivées";
    }
    if (this.hasCadenceAttachmentsTarget) {
      this.cadenceAttachmentsTarget.textContent = detail.attachmentsMode === "frozen"
        ? "figées à la création"
        : "recalculées à chaque génération";
    }
  }

  _renderTimeline(detail) {
    if (!this.hasTimelineTarget) return;
    const items = [];
    if (detail.createdAt) {
      items.push({
        date: detail.createdAt,
        what: "Récurrence créée",
        who: detail.quote && detail.quote.number
          ? `à partir du devis-cadre ${detail.quote.number}`
          : "",
      });
    }
    for (const inv of detail.invoices || []) {
      const date = inv.dateEmission || inv.issuedAt || inv.createdAt;
      items.push({
        date,
        what: `Facture ${inv.number || "—"} générée`,
        who: INVOICE_STATUS_LABELS[inv.status] || "",
      });
    }
    items.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

    this.timelineTarget.replaceChildren();
    if (items.length === 0) {
      const li = document.createElement("li");
      li.className = "muted fs-13";
      li.textContent = "Aucun événement pour le moment.";
      this.timelineTarget.appendChild(li);
      return;
    }
    for (const item of items) {
      const fragment = this.cloneTemplate("tpl-recurrence-timeline-item");
      if (!fragment) continue;
      this.fillSlots(fragment, {
        when: this.formatDate(item.date) || "—",
        what: item.what,
        who: item.who,
      });
      this.timelineTarget.appendChild(fragment);
    }
  }

  _formatEur(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `${n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }

  _formatQty(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString("fr-FR", { maximumFractionDigits: 2 });
  }

  async _loadClient(clientId) {
    if (!this.hasClientNameTarget && !this.hasClientLinesTarget && !this.hasTitleTarget) return;
    if (!clientId) {
      this._setTitleFallback();
      if (this.hasClientNameTarget) this.clientNameTarget.textContent = "—";
      if (this.hasClientLinesTarget) this.clientLinesTarget.textContent = "—";
      return;
    }
    try {
      const client = await api.getClient(clientId);
      this._renderClient(client);
    } catch {
      this._setTitleFallback();
      if (this.hasClientNameTarget) this.clientNameTarget.textContent = "—";
      if (this.hasClientLinesTarget) this.clientLinesTarget.textContent = "—";
    }
  }

  _setTitleFallback() {
    this._title = this._title || "Récurrence";
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = this._title;
    }
  }

  _renderClient(client) {
    this._title = client.legalName || "Récurrence";
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = this._title;
    }
    if (this.hasClientNameTarget) {
      this.clientNameTarget.textContent = client.legalName || "—";
    }
    if (!this.hasClientLinesTarget) return;
    const target = this.clientLinesTarget;
    target.replaceChildren();
    const addresses = (client.addresses || []).filter(a => a.status !== "archived");
    const address = addresses.find(a => a.type === "billing" && a.isDefault)
      || addresses.find(a => a.type === "billing")
      || addresses.find(a => a.isDefault)
      || addresses[0]
      || null;
    if (address) {
      if (address.line1) {
        target.appendChild(document.createTextNode(address.line1));
        target.appendChild(document.createElement("br"));
      }
      if (address.line2) {
        target.appendChild(document.createTextNode(address.line2));
        target.appendChild(document.createElement("br"));
      }
      const cityLine = [address.postalCode, address.city].filter(Boolean).join(" ");
      if (cityLine) {
        target.appendChild(document.createTextNode(cityLine));
        target.appendChild(document.createElement("br"));
      }
    }
    if (client.siret) {
      const siretSpan = document.createElement("span");
      siretSpan.className = "is-mono";
      siretSpan.textContent = `SIRET ${client.siret}`;
      target.appendChild(siretSpan);
      target.appendChild(document.createElement("br"));
    }
    if (client.vatIntracom) {
      const vatSpan = document.createElement("span");
      vatSpan.className = "is-mono";
      vatSpan.textContent = `TVA ${client.vatIntracom}`;
      target.appendChild(vatSpan);
    }
    if (target.childNodes.length === 0) {
      target.textContent = "—";
    }
  }

  _renderHeaderState(status) {
    if (this.hasPauseBtnTarget) {
      this.pauseBtnTarget.hidden = status !== "active";
    }
    if (this.hasResumeBtnTarget) {
      this.resumeBtnTarget.hidden = status !== "paused";
    }
  }

  _renderVatMismatch(visible) {
    if (this.hasVatMismatchBannerTarget) {
      this.vatMismatchBannerTarget.hidden = !visible;
    }
  }

  _renderQuoteEncart(quote) {
    if (!this.hasQuoteEncartTarget) return;
    if (!quote) {
      this.quoteEncartTarget.hidden = true;
      return;
    }
    this.quoteEncartTarget.hidden = false;
    const slot = this.quoteEncartTarget.querySelector('[data-slot="quote-number"]');
    if (slot) slot.textContent = quote.number || quote.id;
    const link = this.quoteEncartTarget.querySelector("a");
    if (link) link.setAttribute("href", `/devis-detail.html?id=${encodeURIComponent(quote.id)}`);
  }

  _renderInvoices(invoices) {
    if (!this.hasInvoicesRowsTarget) return;
    this.invoicesRowsTarget.replaceChildren();
    if (invoices.length === 0) {
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = 5;
      td.className = "muted fs-13";
      td.style.textAlign = "center";
      td.style.padding = "16px 0";
      td.textContent = "Aucune facture générée pour le moment.";
      tr.appendChild(td);
      this.invoicesRowsTarget.appendChild(tr);
      return;
    }
    for (const inv of invoices) {
      const fragment = this.cloneTemplate("tpl-recurrence-invoice-row");
      if (!fragment) continue;
      this.fillSlots(fragment, {
        "invoice-number": inv.number || "—",
        "invoice-date": this.formatShortDate(inv.issuedAt || inv.createdAt) || "—",
        "invoice-amount": inv.amountInclVat || "—",
        "invoice-status": inv.status || "—",
      });
      const link = fragment.querySelector('[data-href="invoice"]');
      if (link) link.setAttribute("href", `/facture-detail.html?id=${encodeURIComponent(inv.id)}`);
      this.invoicesRowsTarget.appendChild(fragment);
    }
  }

  _renderNextDates(dates) {
    if (!this.hasNextDatesListTarget) return;
    this.nextDatesListTarget.replaceChildren();
    if (dates.length === 0) {
      const li = document.createElement("li");
      li.className = "muted fs-13";
      li.textContent = "Aucune prochaine génération prévue.";
      this.nextDatesListTarget.appendChild(li);
      return;
    }
    for (const date of dates) {
      const fragment = this.cloneTemplate("tpl-recurrence-next-date");
      if (!fragment) {
        const li = document.createElement("li");
        li.textContent = this.formatShortDate(date) || date;
        this.nextDatesListTarget.appendChild(li);
        continue;
      }
      this.fillSlots(fragment, { "date": this.formatShortDate(date) || date });
      this.nextDatesListTarget.appendChild(fragment);
    }
  }

  async pause() {
    if (!await this.confirm("Mettre cette récurrence en pause ?")) return;
    try {
      this._detail = await api.post(`/recurring-invoices/${encodeURIComponent(this._recurringId)}/pause`);
      this.showSuccess("Récurrence mise en pause.");
      this._render(this._detail);
    } catch (err) {
      this.showError(err instanceof APIError ? err.message : "Erreur lors de la pause.");
    }
  }

  openResumeModal() {
    const modal = this._ensureModal("tpl-resume-modal", "resume-modal");
    if (modal?.showModal) modal.showModal();
  }

  closeResumeModal() {
    const modal = document.getElementById("resume-modal");
    if (modal?.close) modal.close();
  }

  async resumeToday() {
    this._closeAndResume({});
  }

  async resumeAtDate() {
    const modal = document.getElementById("resume-modal");
    const input = modal?.querySelector('input[type="date"]');
    const dateValue = input?.value;
    if (!dateValue) {
      this.showError("Veuillez saisir une date de reprise.");
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (dateValue < today) {
      this.showError("La date de reprise doit être aujourd'hui ou ultérieure.");
      return;
    }
    this._closeAndResume({ resumeDate: dateValue });
  }

  async _closeAndResume(payload) {
    this.closeResumeModal();
    try {
      this._detail = await api.post(
        `/recurring-invoices/${encodeURIComponent(this._recurringId)}/resume`,
        payload,
      );
      this.showSuccess("Récurrence reprise.");
      this._render(this._detail);
    } catch (err) {
      this.showError(err instanceof APIError ? err.message : "Erreur lors de la reprise.");
    }
  }

  openArchiveModal() {
    const modal = this._ensureModal("tpl-archive-modal", "archive-modal");
    if (modal) {
      const slot = modal.querySelector('[data-slot="recurrence-label"]');
      if (slot) slot.textContent = this._title || "sélectionnée";
    }
    if (modal?.showModal) modal.showModal();
  }

  closeArchiveModal() {
    const modal = document.getElementById("archive-modal");
    if (modal?.close) modal.close();
  }

  async confirmArchive() {
    this.closeArchiveModal();
    try {
      this._detail = await api.post(`/recurring-invoices/${encodeURIComponent(this._recurringId)}/archive`);
      this.showSuccess("Récurrence archivée.");
      this._render(this._detail);
    } catch (err) {
      this.showError(err instanceof APIError ? err.message : "Erreur lors de l'archivage.");
    }
  }

  _ensureModal(tplId, modalId) {
    let modal = document.getElementById(modalId);
    if (modal) return modal;
    const fragment = this.cloneTemplate(tplId);
    if (!fragment) return null;
    document.body.appendChild(fragment);
    return document.getElementById(modalId);
  }
}
